import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { notificationStyles } from './utils';
import LoadingSpinner from './components/LoadingSpinner';
import PasswordField from './components/PasswordField';

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, setToken, isTokenVerified, verifyAndRefreshTokenImmediate, axiosInstance } = useContext(AuthContext);
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAChallenge, setTwoFAChallenge] = useState(null);
  const [verificationNeeded, setVerificationNeeded] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNotification(null);
    setVerificationNeeded(false);
    setTwoFAChallenge(null);
    setTwoFACode('');

    try {
      const encodedForm = new URLSearchParams();
      encodedForm.append('username', formData.username);
      encodedForm.append('password', formData.password);

      const response = await axiosInstance.post('/api/token', encodedForm, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        timeout: 10000,
      });

      const {
        access_token,
        requires_2fa,
        is_onboarded,
        needs_2fa_setup,
        challenge_token,
        setup_token,
      } = response.data;

      if (requires_2fa) {
        if (!challenge_token || access_token) {
          throw new Error('Invalid 2FA challenge response');
        }
        localStorage.removeItem('pending2FASetup');
        localStorage.removeItem('temp_token');
        localStorage.removeItem('temp_username');
        setTwoFAChallenge(challenge_token);
        setShow2FA(true);
        setNotification({ type: 'info', message: 'Please enter your 2FA code' });
        return;
      }

      if (needs_2fa_setup === true) {
        if (!setup_token || access_token) {
          throw new Error('Invalid 2FA setup response');
        }
        localStorage.removeItem('accessToken');
        localStorage.removeItem('token');
        delete axiosInstance.defaults.headers.common.Authorization;
        setToken(null);
        localStorage.setItem('pending2FASetup', 'true');
        localStorage.setItem('temp_token', setup_token);
        navigate('/enable-2fa');
        return;
      }

      if (!access_token) {
        throw new Error('Login did not return an application session');
      }

      localStorage.setItem('accessToken', access_token);
      localStorage.setItem('token', access_token);
      axiosInstance.defaults.headers.common.Authorization = `Bearer ${access_token}`;
      setToken(access_token);

      localStorage.removeItem('pending2FASetup');
      localStorage.removeItem('temp_token');

      navigate(is_onboarded ? '/dashboard' : '/onboarding');
      return;
    } catch (error) {
      const detail = error.response?.data?.detail;
      const unverified = error.response?.status === 403 && (
        detail?.code === 'email_not_verified' || detail === 'Email not verified'
      );
      setVerificationNeeded(unverified);
      setNotification({
        type: 'error',
        message: unverified
          ? 'Verify your email before signing in. Check your inbox or request a new link below.'
          : error.response?.status === 401
            ? 'Incorrect username or password'
            : error.code === 'ECONNABORTED' || !error.response || error.response?.status >= 500
              ? 'Pinngro is temporarily unavailable. Your password was not rejected; please try again shortly.'
              : 'Login could not be completed. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNotification(null);

    try {
      if (!twoFAChallenge) {
        throw new Error('Missing 2FA challenge');
      }
      const response = await axiosInstance.post(
        '/api/verify-2fa',
        { totp_code: twoFACode.trim() },
        { headers: { Authorization: `Bearer ${twoFAChallenge}` } }
      );

      const { access_token, is_onboarded } = response.data;
      if (!access_token) {
        throw new Error('2FA verification did not return an application session');
      }

      localStorage.setItem('accessToken', access_token);
      localStorage.setItem('token', access_token);
      axiosInstance.defaults.headers.common.Authorization = `Bearer ${access_token}`;
      setToken(access_token);
      setTwoFAChallenge(null);
      setShow2FA(false);

      navigate(is_onboarded ? '/dashboard' : '/onboarding');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const code = detail?.code;
      const challengeMustRestart = [
        'challenge_expired',
        'challenge_replayed',
        'challenge_attempts_exceeded',
        'invalid_challenge',
        'invalid_challenge_purpose',
        'session_revoked',
        'account_suspended',
        'two_factor_not_configured',
      ].includes(code);
      if (challengeMustRestart) {
        setTwoFAChallenge(null);
        setTwoFACode('');
        setShow2FA(false);
      }
      setNotification({
        type: 'error',
        message: code === 'account_suspended'
          ? 'Account access is suspended.'
          : challengeMustRestart
            ? 'Your 2FA sign-in challenge is no longer valid. Please sign in again.'
          : code === 'invalid_totp' || error.response?.status === 401
            ? 'Invalid 2FA code'
            : '2FA verification is temporarily unavailable. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    if (!formData.username.trim() || resendCooldown > 0) return;
    setLoading(true);
    try {
      const response = await axiosInstance.post('/api/resend-verification', {
        username: formData.username.trim(),
      });
      setResendCooldown(Number(response.data?.cooldown_seconds || 300));
      setNotification({
        type: 'info',
        message: response.data?.message || 'If verification is still required, a new email will be sent.',
      });
    } catch {
      setNotification({
        type: 'error',
        message: 'Verification email is temporarily unavailable. Please try again later.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && isTokenVerified) {
      verifyAndRefreshTokenImmediate(true).then((isValid) => {
        if (isValid) {
          axiosInstance.get('/api/user/onboarding-status').then((response) => {
            const { is_onboarded } = response.data;
            const redirectPath = is_onboarded ? '/dashboard' : '/onboarding';
            if (location.pathname === '/login' || location.pathname === '/') {
              navigate(redirectPath);
            }
          }).catch(() => {
            setNotification({
              type: 'error',
              message: 'Failed to verify user status',
            });
          });
        }
      }).catch(() => {
        setNotification({
          type: 'error',
          message: 'Session expired',
        });
      });
    }
  }, [token, isTokenVerified, navigate, location.pathname, verifyAndRefreshTokenImmediate, axiosInstance]);

  useEffect(() => {
    const verification = new URLSearchParams(location.search).get('verification');
    if (verification === 'success') {
      setNotification({
        type: 'success',
        message: 'Email verified. Sign in, then enter your authenticator code.',
      });
    } else if (verification === 'invalid_or_expired') {
      setVerificationNeeded(true);
      setNotification({
        type: 'error',
        message: 'That verification link is invalid, expired, or already used. Enter your username below to request a fresh link.',
      });
    } else if (location.state?.from2FASetup) {
      setNotification({
        type: 'success',
        message: '2FA setup complete. Please log in using your 2FA code.',
      });
    } else if (location.state?.sessionExpired) {
      setNotification({
        type: 'info',
        message: 'Your session expired. Please sign in again. Your bots and practice state were not changed.',
      });
    }
  }, [location.search, location.state]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setResendCooldown((current) => Math.max(0, current - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  return (
    <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Login to Pinngro</h2>
        {notification && (
          <div className="mb-4 p-4 rounded-lg border" style={notificationStyles[notification.type]}>
            {notification.message}
          </div>
        )}
        {verificationNeeded && !show2FA && (
          <button
            type="button"
            onClick={resendVerification}
            disabled={loading || resendCooldown > 0}
            className="mb-4 w-full rounded-lg border border-blue-300 px-4 py-2.5 font-semibold text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-950/30"
          >
            {resendCooldown > 0 ? `Email requested · retry in ${resendCooldown}s` : 'Resend verification email'}
          </button>
        )}
        {show2FA ? (
          <form onSubmit={handle2FASubmit}>
            <div className="mb-4">
              <label className="block text-gray-700 dark:text-gray-300 mb-2">2FA Code</label>
              <input
                type="text"
                name="totp_code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value)}
                className="w-full p-2 border rounded-md bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-400 text-gray-900 dark:text-gray-200"
                required
                placeholder="Enter 6-digit code"
              />
            </div>
            <button
              type="submit"
              className="w-full bg-blue-600 text-white p-3 rounded-lg hover:bg-blue-700 transition duration-300 disabled:bg-blue-400"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="sm" tone="light" label="Verifying…" /> : 'Verify 2FA'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-gray-700 dark:text-gray-300 mb-2">Username</label>
              <input
                type="text"
                name="username"
                autoComplete="username"
                value={formData.username}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-400 text-gray-900 dark:text-gray-200"
                required
              />
            </div>
            <div className="mb-6">
              <label htmlFor="login-password" className="block text-gray-700 dark:text-gray-300 mb-2">Password</label>
              <PasswordField
                id="login-password"
                name="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-400 text-gray-900 dark:text-gray-200"
                required
              />
              <div className="mt-2 text-right">
                <Link to="/forgot-password" className="text-sm font-semibold text-blue-600 hover:underline dark:text-blue-300">
                  Forgot your password?
                </Link>
              </div>
            </div>
            <button
              type="submit"
              className="w-full bg-blue-600 text-white p-3 rounded-lg hover:bg-blue-700 transition duration-300 disabled:bg-blue-400"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="sm" tone="light" label="Signing in…" /> : 'Login'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;
